import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { RawIngredient, RawIngredientsState } from '../../types/rawIngredient';
import { rawIngredientApiService } from '../../services/rawIngredientApiService';

// Helper function to create a blank raw ingredient
export const createBlankRawIngredient = (): RawIngredient => {
  return {
    id: uuidv4(),
    name: '',
    lotNumber: '',
    unitSize: '',
    quantity: 0,
    lots: [],
    receivingLogId: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
};

// Async thunks

/**
 * Fetch all raw ingredients from the API
 */
export const fetchRawIngredients = createAsyncThunk(
  'rawIngredients/fetchRawIngredients',
  async () => {
    return await rawIngredientApiService.fetchRawIngredients();
  }
);

/**
 * Create a new raw ingredient
 */
export const createRawIngredientAsync = createAsyncThunk(
  'rawIngredients/createRawIngredient',
  async (rawIngredient: RawIngredient) => {
    return await rawIngredientApiService.createRawIngredient(rawIngredient);
  }
);

/**
 * Update an existing raw ingredient
 */
export const updateRawIngredientAsync = createAsyncThunk(
  'rawIngredients/updateRawIngredient',
  async (rawIngredient: RawIngredient) => {
    return await rawIngredientApiService.updateRawIngredient(rawIngredient);
  }
);

/**
 * Delete a raw ingredient
 */
export const deleteRawIngredientAsync = createAsyncThunk(
  'rawIngredients/deleteRawIngredient',
  async (id: string) => {
    await rawIngredientApiService.deleteRawIngredient(id);
    return id;
  }
);

// Initial state
const initialState: RawIngredientsState = {
  ingredients: [],
  loading: false,
  error: null,
};

const rawIngredientsSlice = createSlice({
  name: 'rawIngredients',
  initialState,
  reducers: {
    // Keep synchronous actions for backward compatibility (deprecated)
    addRawIngredient: (state, action: PayloadAction<RawIngredient>) => {
      state.ingredients.push(action.payload);
    },
    updateRawIngredient: (state, action: PayloadAction<RawIngredient>) => {
      const index = state.ingredients.findIndex(ingredient => ingredient.id === action.payload.id);
      if (index !== -1) {
        state.ingredients[index] = action.payload;
      }
    },
    deleteRawIngredient: (state, action: PayloadAction<string>) => {
      state.ingredients = state.ingredients.filter(ingredient => ingredient.id !== action.payload);
    }
  },
  extraReducers: (builder) => {
    // Fetch raw ingredients
    builder
      .addCase(fetchRawIngredients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRawIngredients.fulfilled, (state, action) => {
        state.loading = false;
        state.ingredients = action.payload;
      })
      .addCase(fetchRawIngredients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch raw ingredients';
      });

    // Create raw ingredient
    builder
      .addCase(createRawIngredientAsync.fulfilled, (state, action) => {
        state.ingredients.push(action.payload);
      })
      .addCase(createRawIngredientAsync.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create raw ingredient';
      });

    // Update raw ingredient
    builder
      .addCase(updateRawIngredientAsync.fulfilled, (state, action) => {
        const index = state.ingredients.findIndex(i => i.id === action.payload.id);
        if (index !== -1) {
          state.ingredients[index] = action.payload;
        }
      })
      .addCase(updateRawIngredientAsync.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update raw ingredient';
      });

    // Delete raw ingredient
    builder
      .addCase(deleteRawIngredientAsync.fulfilled, (state, action) => {
        state.ingredients = state.ingredients.filter(i => i.id !== action.payload);
      })
      .addCase(deleteRawIngredientAsync.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete raw ingredient';
      });
  }
});

export const { addRawIngredient, updateRawIngredient, deleteRawIngredient } = rawIngredientsSlice.actions;
export default rawIngredientsSlice.reducer;
